import React, { useState } from 'react';
import * as XLSX from 'xlsx';

function App() {
  // Allowed columns from Excel
  const allowedColumns = [
    "Item #",
    "Item: Description",
    "Current Inventory: Qty On Hand",
    "Department",
    "Price",
    "Total",
    "bricklink_id",
    "Series",
    "Image"
  ];

  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  // New sort states
  const [sortField, setSortField] = useState("Item #");
  const [sortDirection, setSortDirection] = useState("asc");

  // Responsive Styles
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    background: '#f9f9f9'
  };

  const headingStyle = { textAlign: 'center', color: '#333' };

  const fileInputStyle = {
    margin: '20px 0',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const filterInputStyle = {
    padding: '8px',
    width: '300px',
    marginBottom: '20px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    borderRadius: '4px',
    border: '1px solid #ccc'
  };

  const radioGroupStyle = { textAlign: 'center', marginBottom: '20px' };

  const sortControlsStyle = {
    textAlign: 'center',
    marginBottom: '20px'
  };

  // Responsive grid: auto-fit columns with a minimum width
  const gridContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
    marginBottom: '20px'
  };

  const cardStyle = (isSelected) => ({
    border: isSelected ? '2px solid #007BFF' : '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px',
    cursor: 'pointer',
    background: isSelected ? '#e0f7fa' : '#fff',
    textAlign: 'center'
  });

  const imageStyle = { width: '100%', height: '150px', objectFit: 'contain' };

  const floatingButtonStyle = {
    position: 'fixed',
    right: '20px',
    bottom: '20px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const buttonStyle = {
    background: '#28a745',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    margin: '10px'
  };

  const compareButtonStyle = { ...buttonStyle, background: '#17a2b8' };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  };

  const modalContentStyle = {
    background: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '90%',
    maxHeight: '90%',
    overflow: 'auto'
  };

  // Helper: Group data by Department
  const groupByDepartment = (dataArray) => {
    return dataArray.reduce((groups, item) => {
      const dept = item["Department"] || "Unknown";
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(item);
      return groups;
    }, {});
  };

  // Handle file upload and parse Excel file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      const binaryStr = evt.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length) {
        let headers = jsonData[0].map(header => header.toString().trim());
        headers = headers.filter(header => allowedColumns.includes(header));
        const rows = jsonData.slice(1).map(row => {
          let rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index];
          });
          return rowData;
        });
        // Default sort by bricklink_id on file load (optional)
        rows.sort((a, b) => String(a["bricklink_id"]).localeCompare(String(b["bricklink_id"])));
        setData(rows);
        setSelectedDepartment("All");
        setSelectedRows([]);
        setShowComparison(false);
        // Optionally, set default sort field/direction here if desired.
      }
    };
    reader.readAsBinaryString(file);
  };

  // Handle row click for selection/deselection (up to 10 items)
  const handleRowClick = (row) => {
    const id = row["Item #"];
    const isSelected = selectedRows.some(item => item["Item #"] === id);
    if (isSelected) {
      setSelectedRows(selectedRows.filter(item => item["Item #"] !== id));
    } else {
      if (selectedRows.length < 10) {
        setSelectedRows([...selectedRows, row]);
      } else {
        alert("You can only compare up to 10 rows.");
      }
    }
  };

  // Filter data based on filter text and selected department
  const filteredData = data.filter(row => {
    const matchesText = Object.values(row).some(val =>
      String(val).toLowerCase().includes(filterText.toLowerCase())
    );
    const matchesDept = selectedDepartment === "All" || row["Department"] === selectedDepartment;
    return matchesText && matchesDept;
  });

  // Sort the filtered data based on sortField and sortDirection
  const sortedData = [...filteredData].sort((a, b) => {
    let aVal = a[sortField] || "";
    let bVal = b[sortField] || "";
    if (sortField === "Price") {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Group sorted data by department if "All" is selected; otherwise show only that department
  const groupedData = selectedDepartment === "All" ? groupByDepartment(sortedData) : { [selectedDepartment]: sortedData };

  // Render a card for an item
  const renderCard = (row) => {
    const isSelected = selectedRows.some(item => item["Item #"] === row["Item #"]);
    return (
      <div key={row["Item #"]} onClick={() => handleRowClick(row)} style={cardStyle(isSelected)}>
        {row["Image"] && <img src={row["Image"]} alt="Item" style={imageStyle} />}
        <div style={{ marginTop: '10px', fontWeight: 'bold' }}>{row["Item #"]}</div>
        <div style={{ fontSize: '0.9em' }}>{row["Item: Description"]}</div>
        <div style={{ marginTop: '5px' }}>
          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(row["Price"]) || 0)}
        </div>
        {row["bricklink_id"] && (
          <div style={{ marginTop: '5px' }}>
            <a href={`https://www.bricklink.com/v2/search.page?q=${row["bricklink_id"]}#T=A`} target="_blank" rel="noopener noreferrer">
              BrickLink
            </a>
          </div>
        )}
      </div>
    );
  };

  // Render the comparison view as a modal overlay (unchanged from your previous update)
  const renderComparison = () => {
    if (selectedRows.length === 0) return null;
    return (
      <div style={modalStyle}>
        <div style={modalContentStyle}>
          <h2 style={{ textAlign: 'center' }}>Comparison View</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px' }}>
            {selectedRows.map((row, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '10px',
                  width: '300px',
                  textAlign: 'center',
                  background: '#fefefe'
                }}
              >
                {row["Image"] && (
                  <img
                    src={row["Image"]}
                    alt="Item"
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'contain',
                      marginBottom: '10px'
                    }}
                  />
                )}
                <div style={{ textAlign: 'left' }}>
                  {Object.keys(row)
                    .filter((key) => key !== "Image")
                    .map((key, idx) => (
                      <div key={idx} style={{ marginBottom: '5px' }}>
                        <strong>{key}:</strong>{" "}
                        {key === "Price"
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(row[key]) || 0)
                          : row[key]}
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button onClick={() => setShowComparison(false)} style={buttonStyle}>
              Close Comparison
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Get unique departments for the radio filter
  const departments = Array.from(new Set(data.map(row => row["Department"]).filter(Boolean)));

  return (
    <div style={containerStyle}>
      <h1 style={headingStyle}>Figsorter</h1>
      <p style={{ textAlign: 'center' }}>Upload an Excel file with your minifig data.</p>
      <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} style={fileInputStyle} />
      <input
        type="text"
        placeholder="Filter items..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        style={filterInputStyle}
      />
      <div style={radioGroupStyle}>
        <label>
          <input
            type="radio"
            name="department"
            value="All"
            checked={selectedDepartment === "All"}
            onChange={() => setSelectedDepartment("All")}
          />{" "}
          All
        </label>
        {"  "}
        {departments.map((dept, index) => (
          <label key={index} style={{ marginLeft: '10px' }}>
            <input
              type="radio"
              name="department"
              value={dept}
              checked={selectedDepartment === dept}
              onChange={() => setSelectedDepartment(dept)}
            />{" "}
            {dept}
          </label>
        ))}
      </div>
      {/* Sort Controls */}
      <div style={sortControlsStyle}>
        <label>
          Sort by:{" "}
          <select value={sortField} onChange={(e) => setSortField(e.target.value)}>
            <option value="Item #">Item #</option>
            <option value="Item: Description">Description</option>
            <option value="Price">Price</option>
          </select>
        </label>
        {"  "}
        <label>
          Direction:{" "}
          <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)}>
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
      </div>
      {/* Render grouped items */}
      {Object.keys(groupedData).map((dept, deptIndex) => (
        <div key={deptIndex}>
          <h2 style={{ color: '#007BFF' }}>{dept}</h2>
          <div style={gridContainerStyle}>
            {groupedData[dept].map(item => (
              <div key={item["Item #"]}>{renderCard(item)}</div>
            ))}
          </div>
        </div>
      ))}
      {/* Floating Compare and Clear Buttons */}
      {selectedRows.length > 0 && (
        <div style={floatingButtonStyle}>
          <button onClick={() => setShowComparison(true)} style={compareButtonStyle}>
            Compare Selected ({selectedRows.length})
          </button>
          <button onClick={() => setSelectedRows([])} style={buttonStyle}>
            Clear Selected
          </button>
        </div>
      )}
      {showComparison && renderComparison()}
    </div>
  );
}

export default App;